<template>
  <TopNav :progress="0" active-page="exposicoes" />

  <main class="exposicoes-page">
    <header class="page-header">
      <div class="container">
        <span class="eyebrow archivo-semibold">{{ $t('exposicoesPage.list.eyebrow') }}</span>
        <h1 class="list-title">
          <span class="list-title-pre">{{ $t('exposicoesPage.list.titlePre') }}</span>
          <span class="list-title-main">{{ $t('exposicoesPage.list.titleMain') }}</span>
          <span class="list-title-sub">{{ $t('exposicoesPage.list.titleSub') }}</span>
        </h1>

        <div class="search" :class="{ focused: searchFocused }">
          <svg class="search-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <input
            ref="searchInput"
            v-model="query"
            type="search"
            class="search-input inter-regular"
            :placeholder="$t('exposicoesPage.list.searchPlaceholder')"
            :aria-label="$t('exposicoesPage.list.searchLabel')"
            @focus="searchFocused = true"
            @blur="searchFocused = false"
            @keydown.esc="query = ''"
          />
          <button
            v-if="query"
            type="button"
            class="search-clear"
            :aria-label="$t('exposicoesPage.list.clearSearch')"
            @click="clearSearch"
          >×</button>
          <kbd v-else class="search-kbd archivo-semibold">/</kbd>
        </div>

        <p class="results-count archivo-semibold" aria-live="polite">
          {{ $t('exposicoesPage.list.resultsCount', filtered.length) }}
        </p>
      </div>
    </header>

    <section class="list-section">
      <div class="container">
        <transition-group v-if="filtered.length" name="card" tag="div" class="exhibit-grid">
          <router-link
            v-for="exhibit in filtered"
            :key="exhibit.id"
            :to="`/exposicoes/${exhibit.id}`"
            class="exhibit-card"
          >
            <div class="card-media">
              <img :src="exhibit.image" :alt="$t(`${exhibit.key}.imageAlt`)" loading="lazy" />
              <span class="card-status archivo-semibold">
                <span class="card-status-dot"></span>
                {{ $t(`${exhibit.key}.status`) }}
              </span>
            </div>

            <div class="card-body">
              <p class="card-meta archivo-semibold">
                {{ $t(`${exhibit.key}.location`) }} · {{ exhibit.year }}
              </p>
              <h2 class="card-title bebas-neue-regular">{{ $t(`${exhibit.key}.title`) }}</h2>
              <p class="card-subtitle inter-regular">{{ $t(`${exhibit.key}.subtitle`) }}</p>

              <ul class="card-tags">
                <li v-for="tag in exhibit.tags" :key="tag" class="card-tag archivo-semibold">
                  {{ $t(`exposicoesPage.list.tags.${tag}`) }}
                </li>
              </ul>

              <span class="card-cta archivo-semibold">
                {{ $t('exposicoesPage.list.viewExhibit') }}
                <span class="card-cta-arrow">→</span>
              </span>
            </div>
          </router-link>
        </transition-group>

        <div v-else class="empty-state">
          <p class="empty-title bebas-neue-regular">{{ $t('exposicoesPage.list.emptyTitle') }}</p>
          <p class="empty-text inter-regular">{{ $t('exposicoesPage.list.emptyText', { query }) }}</p>
          <button type="button" class="empty-btn archivo-semibold" @click="clearSearch">
            {{ $t('exposicoesPage.list.clearSearch') }}
          </button>
        </div>
      </div>
    </section>
  </main>

  <FooterGusMachadoYear />
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import TopNav from '@/components/TopNav.vue'
import FooterGusMachadoYear from '@/components/Footer-GusMachadoYear.vue'
import omashuCover from '@/assets/omashu/historia/construcao-geral.jpg'

const { t } = useI18n()

const exhibits = [
  {
    id: 'omashu-express-unip',
    key: 'exposicoesPage.exhibits.omashuExpressUnip',
    image: omashuCover,
    year: 2026,
    tags: ['rollerCoaster', 'thesis', 'promo'],
  },
]

const query = ref('')
const searchFocused = ref(false)
const searchInput = ref(null)

const normalize = (text) =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()

const filtered = computed(() => {
  const terms = normalize(query.value).split(/\s+/).filter(Boolean)
  if (!terms.length) return exhibits

  return exhibits.filter((exhibit) => {
    const haystack = normalize([
      t(`${exhibit.key}.title`),
      t(`${exhibit.key}.subtitle`),
      t(`${exhibit.key}.location`),
      t(`${exhibit.key}.status`),
      String(exhibit.year),
      ...exhibit.tags.map(tag => t(`exposicoesPage.list.tags.${tag}`)),
    ].join(' '))
    return terms.every(term => haystack.includes(term))
  })
})

const clearSearch = () => {
  query.value = ''
  searchInput.value?.focus()
}

const handleShortcut = (e) => {
  const tag = document.activeElement?.tagName
  if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
    e.preventDefault()
    searchInput.value?.focus()
  }
}

onMounted(() => window.addEventListener('keydown', handleShortcut))
onUnmounted(() => window.removeEventListener('keydown', handleShortcut))
</script>

<style scoped>
.exposicoes-page {
  min-height: 100vh;
  text-align: left;
  background: var(--color-bg-dark);
}

.container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ─── Cabeçalho ───────────────────────────────────────── */
.page-header {
  padding: 140px 0 56px;
  background:
    radial-gradient(ellipse 70% 70% at 90% 0%, rgba(37, 99, 235, 0.3), transparent 70%),
    radial-gradient(ellipse 60% 50% at 0% 100%, rgba(251, 191, 36, 0.1), transparent 70%),
    var(--color-bg-dark);
  border-bottom: 1px solid rgba(229, 231, 235, 0.08);
}

.eyebrow {
  display: flex;
  align-items: center;
  color: var(--color-yellow);
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 18px;
}

.eyebrow::before {
  content: "";
  flex: 0 0 auto;
  width: 24px;
  height: 2px;
  margin-right: 10px;
  background: currentColor;
}

.list-title {
  display: inline-flex;
  flex-direction: column;
  margin: 0 0 40px;
  cursor: default;
  transform-origin: left center;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.list-title:hover {
  transform: scale(1.03);
}

.list-title-pre {
  font-family: "Archivo", sans-serif;
  font-weight: 200;
  font-size: 26px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--color-light);
  line-height: 1;
}

.list-title-main {
  font-family: "Bebas Neue", sans-serif;
  font-size: 120px;
  line-height: 0.92;
  color: var(--color-yellow);
  margin: 6px 0 10px;
  transition: text-shadow 0.35s ease;
}

.list-title:hover .list-title-main {
  text-shadow: 0 0 32px rgba(251, 191, 36, 0.35);
}

.list-title-sub {
  font-family: "Inter", sans-serif;
  font-weight: 300;
  font-size: 18px;
  color: rgba(229, 231, 235, 0.7);
}

/* ─── Pesquisa ────────────────────────────────────────── */
.search {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 620px;
  height: 56px;
  padding: 0 14px 0 18px;
  border-radius: 14px;
  background: rgba(229, 231, 235, 0.06);
  border: 1px solid rgba(229, 231, 235, 0.14);
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.search:hover {
  border-color: rgba(229, 231, 235, 0.28);
}

.search.focused {
  background: rgba(229, 231, 235, 0.09);
  border-color: var(--color-blue);
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.25);
}

.search-icon {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  fill: none;
  stroke: rgba(229, 231, 235, 0.6);
  stroke-width: 2;
  stroke-linecap: round;
}

.search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--color-light);
  font-size: 16px;
  font-weight: 300;
}

.search-input::placeholder {
  color: rgba(229, 231, 235, 0.45);
}

.search-input::-webkit-search-cancel-button {
  display: none;
}

.search-kbd {
  flex: 0 0 auto;
  min-width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid rgba(229, 231, 235, 0.2);
  color: rgba(229, 231, 235, 0.55);
  font-size: 13px;
}

.search-clear {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 50%;
  background: rgba(229, 231, 235, 0.1);
  color: var(--color-light);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;
}

.search-clear:hover {
  background: rgba(229, 231, 235, 0.2);
}

.results-count {
  margin: 16px 0 0;
  color: rgba(229, 231, 235, 0.5);
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

/* ─── Lista ───────────────────────────────────────────── */
.list-section {
  padding: 56px 0 96px;
}

.exhibit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.exhibit-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #16202D;
  border: 1px solid rgba(229, 231, 235, 0.08);
  text-decoration: none;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.exhibit-card:hover {
  transform: translateY(-6px);
  border-color: rgba(251, 191, 36, 0.45);
  box-shadow: 0 24px 48px -20px rgba(0, 0, 0, 0.7);
}

.card-media {
  position: relative;
  aspect-ratio: 16 / 10;
  max-width: 100%;
  overflow: hidden;
}

.card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s ease;
}

.exhibit-card:hover .card-media img {
  transform: scale(1.06);
}

.card-media::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(22, 32, 45, 0.9) 100%);
}

.card-status {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--color-yellow);
  color: var(--color-bg-dark);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.card-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-bg-dark);
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {
  0%   { box-shadow: 0 0 0 0 rgba(30, 42, 58, 0.5); }
  100% { box-shadow: 0 0 0 8px rgba(30, 42, 58, 0); }
}

.card-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 22px 24px 24px;
}

.card-meta {
  margin: 0 0 8px;
  color: var(--color-yellow);
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.card-title {
  margin: 0 0 8px;
  color: var(--color-light);
  font-size: 36px;
  line-height: 1;
}

.card-subtitle {
  margin: 0 0 18px;
  color: rgba(229, 231, 235, 0.7);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.55;
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
  margin: 0 0 22px;
  padding: 0;
}

.card-tag {
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.16);
  border: 1px solid rgba(37, 99, 235, 0.35);
  color: #A9C2F7;
  font-size: 11px;
  letter-spacing: 0.04em;
}

.card-cta {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--color-light);
  font-size: 13px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.card-cta-arrow {
  color: var(--color-yellow);
  transition: transform 0.25s ease;
}

.exhibit-card:hover .card-cta-arrow {
  transform: translateX(6px);
}

/* ─── Estado vazio ────────────────────────────────────── */
.empty-state {
  padding: 72px 24px;
  text-align: center;
  border: 1px dashed rgba(229, 231, 235, 0.18);
  border-radius: 18px;
}

.empty-title {
  margin: 0 0 8px;
  color: var(--color-light);
  font-size: 40px;
}

.empty-text {
  margin: 0 0 24px;
  color: rgba(229, 231, 235, 0.65);
  font-size: 15px;
  font-weight: 300;
  overflow-wrap: anywhere;
}

.empty-btn {
  padding: 12px 22px;
  border: 0;
  border-radius: 999px;
  background: var(--color-yellow);
  color: var(--color-bg-dark);
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.empty-btn:hover {
  transform: scale(1.04);
}

/* ─── Transições ──────────────────────────────────────── */
.card-enter-active,
.card-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.card-enter-from,
.card-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

/* ─── Mobile ──────────────────────────────────────────── */
@media (max-aspect-ratio: 3/4) {
  .container {
    padding: 0 20px;
  }

  .page-header {
    padding: 110px 0 40px;
  }

  .list-title-pre {
    font-size: 18px;
  }

  .list-title-main {
    font-size: 72px;
  }

  .list-title-sub {
    font-size: 15px;
  }

  .search {
    height: 52px;
  }

  .search-kbd {
    display: none;
  }

  .exhibit-grid {
    grid-template-columns: 1fr;
  }

  .list-section {
    padding: 40px 0 72px;
  }
}
</style>
